import { useState, useRef, useEffect, useCallback } from 'react';
import { format, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addMonths, subMonths, isSameDay, isSameMonth, isBefore, startOfDay } from 'date-fns';
import { ChevronLeft, ChevronRight, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const ACCENT = '#9b6035';
const ACCENT_LIGHT = '#c4956a';
const BG = '#f8f7f4';
const TEXT_DARK = '#333333';
const TEXT_MID = '#666666';
const TEXT_LIGHT = '#b5b5b5';
const BORDER = '#e0e0e0';
const DAY_LABELS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

function CalendarGrid({ currentMonth, selectedDate, onSelect, disabledBefore }) {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const today = startOfDay(new Date());

  const weeks = [];
  let day = calStart;
  while (day <= calEnd) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(day));
      day = addDays(day, 1);
    }
    weeks.push(week);
  }

  return (
    <div>
      {/* Day labels */}
      <div className="grid grid-cols-7 mb-1">
        {DAY_LABELS.map((d) => (
          <div key={d} className="text-center py-2 text-xs font-semibold tracking-wide" style={{ color: TEXT_MID }}>
            {d}
          </div>
        ))}
      </div>
      {/* Date grid */}
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7">
          {week.map((d, di) => {
            const inMonth = isSameMonth(d, currentMonth);
            const isSelected = selectedDate && isSameDay(d, selectedDate);
            const disabled = disabledBefore ? isBefore(d, disabledBefore) : isBefore(d, today);
            const isPast = isBefore(d, today) && !isSameDay(d, today);

            return (
              <button
                key={di}
                type="button"
                onClick={() => !disabled && inMonth && onSelect(d)}
                disabled={disabled || !inMonth}
                className={cn(
                  'relative flex flex-col items-center justify-center py-2.5 text-sm transition-all duration-150 rounded-md',
                  !inMonth && 'invisible',
                  inMonth && !isSelected && !disabled && 'hover:bg-black/5 cursor-pointer',
                  disabled && inMonth && 'cursor-not-allowed',
                )}
                style={{
                  backgroundColor: isSelected ? ACCENT : 'transparent',
                  color: isSelected ? '#fff' : disabled ? TEXT_LIGHT : isPast ? TEXT_LIGHT : TEXT_DARK,
                }}
                data-testid={`cal-day-${format(d, 'yyyy-MM-dd')}`}
              >
                <span className="text-sm font-medium leading-none">{format(d, 'd')}</span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export function ReservationSearch({
  onSearch,
  loading = false,
  showTitle = true,
  showGuests = true,
  showProperty = false,
  properties = [],
  selectedPropertyId = '',
  onPropertyChange,
  className = '',
  compact = false,
}) {
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [rooms, setRooms] = useState(1);
  const [guests, setGuests] = useState(2);
  const [activeField, setActiveField] = useState(null); // 'checkin' | 'checkout' | null
  const [calMonth, setCalMonth] = useState(new Date());
  const [guestsOpen, setGuestsOpen] = useState(false);
  const containerRef = useRef(null);

  const today = startOfDay(new Date());

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setActiveField(null);
        setGuestsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleDateSelect = useCallback((date) => {
    if (activeField === 'checkin') {
      setCheckIn(date);
      if (!checkOut || checkOut <= date) {
        setCheckOut(addDays(date, 1));
      }
      setActiveField('checkout');
      setCalMonth(date);
    } else if (activeField === 'checkout') {
      if (checkIn && date <= checkIn) {
        setCheckIn(date);
        setCheckOut(addDays(date, 1));
        setCalMonth(date);
      } else {
        setCheckOut(date);
        setActiveField(null);
      }
    }
  }, [activeField, checkIn, checkOut]);

  const handleSearch = () => {
    if (!checkIn || !checkOut) return;
    onSearch({
      checkIn,
      checkOut,
      guests: guests.toString(),
      rooms,
      propertyId: selectedPropertyId,
    });
  };

  const formatSelectedDate = (date) => {
    if (!date) return '—';
    return format(date, 'd MMMM');
  };

  return (
    <div ref={containerRef} className={cn('relative', className)} data-testid="reservation-search">
      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: BG, border: `1px solid ${BORDER}` }}>
        {/* Title */}
        {showTitle && (
          <div className="pt-8 pb-2 text-center">
            <h2 className="font-serif text-2xl sm:text-3xl font-normal tracking-tight" style={{ color: TEXT_DARK }}>
              Make a Reservation
            </h2>
          </div>
        )}

        {/* Input Fields */}
        <div className={cn('px-6 pb-4', showTitle ? 'pt-4' : 'pt-6')}>
          {/* Property Selector (optional) */}
          {showProperty && properties.length > 0 && (
            <div className="mb-5">
              <label className="block text-xs font-semibold tracking-[0.15em] uppercase mb-2" style={{ color: TEXT_MID }}>
                PROPERTY
              </label>
              <select
                value={selectedPropertyId}
                onChange={(e) => onPropertyChange?.(e.target.value)}
                className="w-full bg-transparent text-base pb-2 outline-none cursor-pointer appearance-none"
                style={{ color: TEXT_DARK, borderBottom: `1px solid ${BORDER}` }}
                data-testid="reservation-property-select"
              >
                <option value="">All Properties</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className={cn('grid gap-4', compact ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2')}>
            {/* Check In */}
            <div
              className="cursor-pointer group"
              onClick={() => { setActiveField(activeField === 'checkin' ? null : 'checkin'); setCalMonth(checkIn || new Date()); setGuestsOpen(false); }}
              data-testid="reservation-checkin"
            >
              <label className="block text-xs font-semibold tracking-[0.15em] uppercase mb-2 transition-colors" style={{ color: activeField === 'checkin' ? ACCENT : TEXT_MID }}>
                CHECK IN
              </label>
              <div
                className="text-lg pb-2 transition-colors"
                style={{
                  color: checkIn ? TEXT_DARK : TEXT_LIGHT,
                  borderBottom: `2px solid ${activeField === 'checkin' ? ACCENT : BORDER}`,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {formatSelectedDate(checkIn)}
              </div>
            </div>

            {/* Check Out */}
            <div
              className="cursor-pointer group"
              onClick={() => { setActiveField(activeField === 'checkout' ? null : 'checkout'); setCalMonth(checkOut || checkIn || new Date()); setGuestsOpen(false); }}
              data-testid="reservation-checkout"
            >
              <label className="block text-xs font-semibold tracking-[0.15em] uppercase mb-2 transition-colors" style={{ color: activeField === 'checkout' ? ACCENT : TEXT_MID }}>
                CHECK OUT
              </label>
              <div
                className="text-lg pb-2 transition-colors"
                style={{
                  color: checkOut ? TEXT_DARK : TEXT_LIGHT,
                  borderBottom: `2px solid ${activeField === 'checkout' ? ACCENT : BORDER}`,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {formatSelectedDate(checkOut)}
              </div>
            </div>
          </div>

          {/* Rooms & Guests */}
          {showGuests && (
            <div className="mt-5 relative">
              <label className="block text-xs font-semibold tracking-[0.15em] uppercase mb-2" style={{ color: guestsOpen ? ACCENT : TEXT_MID }}>
                ROOMS & GUESTS
              </label>
              <div
                className="flex items-center justify-between cursor-pointer pb-2"
                style={{ borderBottom: `2px solid ${guestsOpen ? ACCENT : BORDER}` }}
                onClick={() => { setGuestsOpen(!guestsOpen); setActiveField(null); }}
                data-testid="reservation-guests"
              >
                <span className="text-lg" style={{ color: TEXT_DARK, fontFamily: "'DM Sans', sans-serif" }}>
                  {rooms} {rooms === 1 ? 'Room' : 'Rooms'}, {guests} {guests === 1 ? 'Guest' : 'Guests'}
                </span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', guestsOpen && 'rotate-180')} style={{ color: TEXT_MID }} />
              </div>
              {guestsOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border z-50 p-4 space-y-4" style={{ borderColor: BORDER }}>
                  {/* Rooms stepper */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium" style={{ color: TEXT_DARK }}>Rooms</span>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setRooms(Math.max(1, rooms - 1)); }}
                        disabled={rooms <= 1}
                        className="w-8 h-8 rounded-full border flex items-center justify-center text-lg leading-none transition-colors disabled:opacity-30"
                        style={{ borderColor: BORDER, color: TEXT_DARK }}
                        data-testid="rooms-decrease"
                      >
                        −
                      </button>
                      <span className="w-6 text-center text-sm font-semibold" style={{ color: TEXT_DARK }} data-testid="rooms-count">{rooms}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setRooms(Math.min(10, rooms + 1)); }}
                        disabled={rooms >= 10}
                        className="w-8 h-8 rounded-full border flex items-center justify-center text-lg leading-none transition-colors disabled:opacity-30"
                        style={{ borderColor: BORDER, color: TEXT_DARK }}
                        data-testid="rooms-increase"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  {/* Guests stepper */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium" style={{ color: TEXT_DARK }}>Guests</span>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setGuests(Math.max(1, guests - 1)); }}
                        disabled={guests <= 1}
                        className="w-8 h-8 rounded-full border flex items-center justify-center text-lg leading-none transition-colors disabled:opacity-30"
                        style={{ borderColor: BORDER, color: TEXT_DARK }}
                        data-testid="guests-decrease"
                      >
                        −
                      </button>
                      <span className="w-6 text-center text-sm font-semibold" style={{ color: TEXT_DARK }} data-testid="guests-count">{guests}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setGuests(Math.min(20, guests + 1)); }}
                        disabled={guests >= 20}
                        className="w-8 h-8 rounded-full border flex items-center justify-center text-lg leading-none transition-colors disabled:opacity-30"
                        style={{ borderColor: BORDER, color: TEXT_DARK }}
                        data-testid="guests-increase"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Book Now Button */}
          <button
            type="button"
            onClick={handleSearch}
            disabled={loading || !checkIn || !checkOut}
            className="w-full mt-6 py-3.5 text-sm font-semibold tracking-[0.2em] uppercase transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              backgroundColor: ACCENT,
              color: '#fff',
              letterSpacing: '0.2em',
            }}
            onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = ACCENT_LIGHT; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ACCENT; }}
            data-testid="reservation-search-btn"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mx-auto" />
            ) : (
              'BOOK NOW \u00BB'
            )}
          </button>
        </div>

        {/* Calendar Dropdown */}
        {activeField && (
          <div className="border-t px-6 pb-6 pt-4" style={{ borderColor: BORDER, backgroundColor: '#fff' }} data-testid="reservation-calendar">
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={() => setCalMonth(subMonths(calMonth, 1))}
                className="p-1 hover:opacity-70 transition-opacity"
                data-testid="cal-prev-month"
              >
                <ChevronLeft className="h-5 w-5" style={{ color: TEXT_DARK }} />
              </button>
              <h3 className="font-serif text-xl font-normal" style={{ color: TEXT_DARK }}>
                {format(calMonth, 'MMMM yyyy')}
              </h3>
              <button
                type="button"
                onClick={() => setCalMonth(addMonths(calMonth, 1))}
                className="p-1 hover:opacity-70 transition-opacity"
                data-testid="cal-next-month"
              >
                <ChevronRight className="h-5 w-5" style={{ color: TEXT_DARK }} />
              </button>
            </div>

            {/* Calendar */}
            <CalendarGrid
              currentMonth={calMonth}
              selectedDate={activeField === 'checkin' ? checkIn : checkOut}
              onSelect={handleDateSelect}
              disabledBefore={activeField === 'checkout' ? (checkIn || today) : today}
            />

            {/* Legend */}
            <div className="flex items-center gap-6 mt-4 pt-3" style={{ borderTop: `1px solid ${BORDER}` }}>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: ACCENT }} />
                <span className="text-xs" style={{ color: TEXT_MID }}>Selected</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-sm border" style={{ borderColor: TEXT_LIGHT, opacity: 0.5 }} />
                <span className="text-xs" style={{ color: TEXT_MID }}>Unavailable</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
